import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from '@firebase/auth';
import {
  BanknotesIcon,
  Bars3Icon,
  BellIcon,
  BookmarkIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ChevronRightIcon,
  DocumentIcon,
  FilmIcon,
  HandRaisedIcon,
  HomeIcon,
  MegaphoneIcon,
  MoonIcon,
  PencilSquareIcon,
  PlusIcon,
  Squares2X2Icon,
  SunIcon,
  UsersIcon,
  VideoCameraIcon,
  XMarkIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';
import ProfileCompletionModal from './ProfileCompletionModal';
import SearchBar from './SearchBar';
import ImageWithFallback from './ImageWithFallback';
import BrandLogo from './BrandLogo';
import { auth } from '../firebase';
import { getUserMe, PROFILE_UPDATED_EVENT, UserProfileDto, waitForAuthUser } from '../utils/secureFunctions';
import { useInboxSignal } from '../utils/inboxSignal';
import { getProfileUrl } from '../utils/urls';
import { useBrandingConfig } from '../utils/siteConfig';
import '../styles/navigation.css';

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

type NavItem = {
  label: string;
  path: string;
  icon: IconComponent;
  description?: string;
};

const NAV_SECTIONS: Array<{ title: string; subtitle: string; items: NavItem[] }> = [
  {
    title: 'Create & Share',
    subtitle: 'Tell stories across formats',
    items: [
      { label: 'Home timeline', path: '/home', icon: HomeIcon, description: 'Catch up quickly' },
      { label: 'Pulse', path: '/pulse', icon: FilmIcon, description: 'Share the vibe' },
      { label: 'Stories', path: '/stories', icon: BookOpenIcon, description: 'Share short moments' },
      { label: 'Testimonies', path: '/testimony', icon: HandRaisedIcon, description: 'Boost someone\'s faith' }
    ]
  },
  {
    title: 'Community & Tools',
    subtitle: 'Grow together faster',
    items: [
      { label: 'Groups', path: '/groups', icon: UsersIcon, description: 'Find your circles' },
      { label: 'Saved', path: '/saved', icon: BookmarkIcon, description: 'Quick stash' },
      { label: 'Pages', path: '/pages', icon: DocumentIcon, description: 'Manage ministries' },
      { label: 'Go Live', path: '/go-live', icon: VideoCameraIcon, description: 'Schedule a stream' }
    ]
  },
  {
    title: 'Monetize & Promote',
    subtitle: 'Track reach & spend',
    items: [
      { label: 'Earnings', path: '/earnings', icon: BanknotesIcon, description: 'See payouts' },
      { label: 'My Ads', path: '/advertiser-dashboard', icon: ChartBarIcon, description: 'Campaign data' },
      { label: 'Create Ad', path: '/create-ad', icon: MegaphoneIcon, description: 'Boost instantly' }
    ]
  }
];

const SUPPORT_LINKS: NavItem[] = [
  { label: 'Creator studio', path: '/creator-studio', icon: Squares2X2Icon, description: 'Advanced dashboards' },
  { label: 'Guides & docs', path: '/docs', icon: DocumentIcon, description: 'How Belivah works' },
  { label: 'Playbook', path: '/playbook', icon: DocumentIcon, description: 'Grow your ministry' },
  { label: 'Help', path: '/help', icon: DocumentIcon, description: 'FAQs & support' },
  { label: 'Account settings', path: '/settings', icon: PencilSquareIcon, description: 'Preferences & privacy' }
];

const ALL_NAV_ITEMS: NavItem[] = NAV_SECTIONS.flatMap((section) => section.items);
const DONATE_URL = 'https://www.paypal.com/ncp/payment/H3LCCM46CURP4';

const TopNavigation: React.FC = () => {
  const navigate = useNavigate();
  const { config } = useBrandingConfig('published');
  const [isMobile, setIsMobile] = useState(() => {
    try {
      return window.innerWidth <= 768;
    } catch {
      return false;
    }
  });
  const [userPhoto, setUserPhoto] = useState('');
  const [username, setUsername] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => (localStorage.getItem('belivah_theme') as 'light' | 'dark') || 'light');
  const [unreadCount, setUnreadCount] = useState(0);
  const inbox = useInboxSignal();

  useEffect(() => {
    const onResize = () => {
      try {
        setIsMobile(window.innerWidth <= 768);
      } catch {}
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let active = true;
    let usernameUpdated = false;
    const loadUser = async () => {
      const user = auth.currentUser || await waitForAuthUser();
      if (!user || !active) return;
      setUserPhoto(user.photoURL || '');
      try {
        const token = await user.getIdTokenResult();
        if (!active) return;
        const claims: any = token?.claims || {};
        const role = String(claims.role || '').toLowerCase();
        setIsAdmin(claims.admin === true || role === 'admin' || role === 'moderator' || role === 'super_admin');
        const me = await getUserMe().catch(() => null);
        if (!active) return;
        if (!usernameUpdated) setUsername(me?.profile?.username || null);
        if (me?.profile?.photoURL) setUserPhoto(me.profile.photoURL);
        const dbRole = String((me?.profile as { role?: string } | undefined)?.role || '').toLowerCase();
        if (dbRole === 'admin' || dbRole === 'moderator' || dbRole === 'super_admin') setIsAdmin(true);
      } catch (err) {
        console.warn('Could not load user document:', (err as Error)?.message);
      }
    };
    loadUser();

    const handleClickOutside = () => {
      setShowProfileDropdown(false);
      setShowCreateMenu(false);
      setShowMenuDropdown(false);
    };
    const onProfileUpdated = (e: Event) => {
      const p = (e as CustomEvent<UserProfileDto>).detail;
      if (p && 'photoURL' in p) setUserPhoto(p.photoURL || '');
      if (p && 'username' in p) {
        usernameUpdated = true;
        setUsername(p.username || null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () => {
      active = false;
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    };
  }, []);

  useEffect(() => {
    // Legacy notifications removed. Will be replaced with SSE/WS + Redis fanout.
    setUnreadCount(0);
  }, []);

  useEffect(() => {
    try {
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('belivah_theme', theme);
    } catch {}
  }, [theme]);

  useEffect(() => {
    if (!isMobile || !showMenuDropdown) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobile, showMenuDropdown]);

  const toggleTheme = () => setTheme((t) => (t === 'light' ? 'dark' : 'light'));

  const handleNavigate = (path: string) => {
    setShowMenuDropdown(false);
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const iconButtonStyles = useMemo(() => ({
    ...styles.iconButton,
    width: isMobile ? 44 : 44,
    height: isMobile ? 44 : 44,
    padding: isMobile ? 8 : 8,
    borderRadius: 999,
    backgroundColor: isMobile ? 'rgba(7,43,44,0.08)' : 'transparent',
    boxShadow: isMobile ? 'var(--shadow-sm)' : 'none'
  }), [isMobile]);

  return (
    <>
      <nav style={styles.nav}>
        <div style={styles.navContent}>
          <div style={{ ...styles.leftSection, ...(isMobile ? { width: 'auto' } : {}) }}>
            <Link to="/home" style={{ ...styles.logo, ...(isMobile ? { fontSize: 20 } : {}) }}>
              {config.logoUrl ? (
                <img src={config.logoUrl} alt={config.brandName} style={{ height: isMobile ? 24 : 28, objectFit: 'contain' }} />
              ) : (
                <BrandLogo text={config.brandName || 'Belivah'} showIcon={false} size={isMobile ? 28 : 34} color="var(--color-primary-700)" />
              )}
            </Link>
          </div>

          <div style={{ ...styles.centerSection, ...(isMobile ? { display: 'none' } : {}) }}>
            <SearchBar />
          </div>

          <div style={{ ...styles.rightSection, ...(isMobile ? { width: 'auto', gap: 4, minWidth: 0 } : {}) }}>
            {!isMobile && (
              <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setShowCreateMenu((prev) => !prev)}
                  style={styles.createButton}
                >
                  <PlusIcon style={{ width: 18, height: 18, color: '#FFFFFF' }} />
                  <span style={{ color: '#FFFFFF' }}>Create</span>
                </button>
                {showCreateMenu && (
                  <div style={styles.dropdown}>
                    <div style={styles.dropdownItem} onClick={() => { setShowCreateMenu(false); navigate('/home'); }}>
                      <PencilSquareIcon style={styles.dropdownIcon} />
                      Post
                    </div>
                    <div style={styles.dropdownItem} onClick={() => { setShowCreateMenu(false); navigate('/pulse'); }}>
                      <FilmIcon style={styles.dropdownIcon} />
                      Pulse Video
                    </div>
                    <div style={styles.dropdownItem} onClick={() => { setShowCreateMenu(false); navigate('/testimony'); }}>
                      <HandRaisedIcon style={styles.dropdownIcon} />
                      Testimony
                    </div>
                    <div style={styles.dropdownItem} onClick={() => { setShowCreateMenu(false); navigate('/stories'); }}>
                      <DocumentIcon style={styles.dropdownIcon} />
                      Story
                    </div>
                    <div style={styles.dropdownItem} onClick={() => { setShowCreateMenu(false); navigate('/live'); }}>
                      <VideoCameraIcon style={styles.dropdownIcon} />
                      Live
                    </div>
                    <div style={styles.dropdownItem} onClick={() => { setShowCreateMenu(false); navigate('/create-ad'); }}>
                      <MegaphoneIcon style={styles.dropdownIcon} />
                      Create Ad
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
              <button style={iconButtonStyles} aria-label="Open menu" onClick={() => setShowMenuDropdown((prev) => !prev)}>
                <Bars3Icon style={{ width: isMobile ? 28 : 28, height: isMobile ? 28 : 28, color: 'var(--color-text-primary)' }} />
              </button>
              {showMenuDropdown && (
                isMobile ? createPortal(
                  <div
                    className="belivah-sheet-overlay"
                    style={styles.mobileOverlay}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenuDropdown(false);
                    }}
                  >
                    <div style={styles.mobileSheet} onClick={(e) => e.stopPropagation()}>
                      <div style={styles.mobileSheetHeader}>
                        <div style={styles.mobileSheetTitle}>Browse Belivah</div>
                        <button
                          type="button"
                          style={styles.mobileCloseButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowMenuDropdown(false);
                          }}
                          aria-label="Close menu"
                        >
                          <XMarkIcon style={{ width: 22, height: 22, color: 'var(--color-text-primary)' }} />
                        </button>
                      </div>
                      <div style={styles.mobileSheetBody}>
                        {NAV_SECTIONS.map((section) => (
                          <div key={section.title} style={styles.mobileSection}>
                            <div style={styles.mobileSectionHeader}>
                              <div style={styles.mobileSectionTitle}>{section.title}</div>
                              <div style={styles.mobileSectionSubtitle}>{section.subtitle}</div>
                            </div>
                            <div style={styles.mobileSectionGrid}>
                              {section.items.map((item) => (
                                <div key={item.label} style={styles.mobileTile} onClick={() => handleNavigate(item.path)}>
                                  <div style={styles.mobileTileIconWrap}>
                                    <item.icon style={styles.mobileTileIcon} />
                                  </div>
                                  <div style={styles.mobileTileText}>
                                    <div style={styles.mobileTileLabel}>{item.label}</div>
                                    {item.description && (
                                      <div style={styles.mobileTileDescription}>{item.description}</div>
                                    )}
                                  </div>
                                  <ChevronRightIcon style={styles.mobileTileChevron} />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                        <div style={styles.supportLinks}>
                          {SUPPORT_LINKS.map((link) => (
                            <div key={link.label} style={styles.supportLink} onClick={() => handleNavigate(link.path)}>
                              <div style={styles.supportLinkIconWrap}>
                                <link.icon style={styles.supportLinkIcon} />
                              </div>
                              <div style={styles.supportLinkText}>
                                <div style={styles.supportLinkLabel}>{link.label}</div>
                                {link.description && (
                                  <div style={styles.supportLinkSubtitle}>{link.description}</div>
                                )}
                              </div>
                              <ChevronRightIcon style={styles.mobileTileChevron} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>,
                  document.body
                ) : (
                  <div
                    style={{
                      ...styles.menuDropdown,
                      minWidth: 420,
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 12,
                      padding: 14
                    }}
                  >
                    {ALL_NAV_ITEMS.map((item) => (
                      <div key={item.label} style={styles.menuTile} onClick={() => handleNavigate(item.path)}>
                        <item.icon style={styles.menuTileIcon} />
                        <span style={styles.menuTileLabel}>{item.label}</span>
                      </div>
                    ))}
                    {SUPPORT_LINKS.map((item) => (
                      <div key={item.label} style={styles.menuTile} onClick={() => handleNavigate(item.path)}>
                        <item.icon style={styles.menuTileIcon} />
                        <span style={styles.menuTileLabel}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>

            <button
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{
                ...styles.themeToggle,
                ...(isMobile ? { padding: 8, borderRadius: 999 } : {})
              }}
              onClick={toggleTheme}
            >
              {theme === 'dark' ? (
                <SunIcon style={{ width: 18, height: 18, color: 'var(--color-primary-700)' }} />
              ) : (
                <MoonIcon style={{ width: 18, height: 18, color: 'var(--color-primary-700)' }} />
              )}
            </button>

            <Link to="/notifications" style={{ ...iconButtonStyles, position: 'relative' }}>
              <BellIcon style={{ width: isMobile ? 28 : 26, height: isMobile ? 28 : 26, color: 'var(--color-text-primary)' }} />
              {unreadCount > 0 && (
                <span style={styles.unreadBadge}>{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </Link>

            <Link to="/messages" style={{ ...iconButtonStyles, position: 'relative' }} aria-label={inbox.ringing ? 'Incoming call' : inbox.unread ? `${inbox.unread} unread messages` : 'Messages'}>
              <ChatBubbleLeftRightIcon
                className={inbox.ringing ? 'inbox-ringing' : undefined}
                style={{ width: isMobile ? 28 : 26, height: isMobile ? 28 : 26, color: inbox.ringing ? '#16a34a' : 'var(--color-text-primary)' }}
              />
              {inbox.ringing ? (
                <span className="inbox-ring-badge" style={{ ...styles.unreadBadge, background: '#16a34a' }}>📞</span>
              ) : inbox.unread > 0 && (
                <span style={styles.unreadBadge}>{inbox.unread > 99 ? '99+' : inbox.unread}</span>
              )}
            </Link>

            <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
              <ImageWithFallback
                src={userPhoto}
                alt="Profile"
                style={{ ...styles.profileAvatar, ...(isMobile ? { width: 34, height: 34 } : {}) }}
                onClick={() => setShowProfileDropdown((prev) => !prev)}
              />
              {showProfileDropdown && (
                <div
                  style={{
                    ...styles.dropdown,
                    ...(isMobile ? { right: 8, left: 'auto', minWidth: 220, maxWidth: '92vw' } : {})
                  }}
                >
                  <div
                    style={styles.dropdownItem}
                    onClick={() => {
                      setShowProfileDropdown(false);
                      if (auth.currentUser) navigate(getProfileUrl({ id: auth.currentUser.uid, username }));
                    }}
                  >
                    My Profile
                  </div>
                  <div
                    style={styles.dropdownItem}
                    onClick={() => {
                      setShowProfileDropdown(false);
                      navigate('/pages');
                    }}
                  >
                    Pages
                  </div>
                  <div style={styles.dropdownItem} onClick={() => { setShowProfileDropdown(false); navigate('/settings'); }}>
                    Settings
                  </div>
                  <a
                    href={DONATE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ ...styles.dropdownItem, display: 'block', textDecoration: 'none', color: 'inherit' }}
                    onClick={() => setShowProfileDropdown(false)}
                  >
                    Donate
                  </a>
                  <div style={styles.dropdownItem} onClick={() => { setShowProfileDropdown(false); navigate('/saved'); }}>
                    Saved Items
                  </div>
                  {isAdmin && (
                    <>
                      <div style={styles.dropdownDivider} />
                      <div style={styles.dropdownItem} onClick={() => { setShowProfileDropdown(false); navigate('/studio'); }}>
                        Admin Dashboard
                      </div>
                      <div style={styles.dropdownItem} onClick={() => { setShowProfileDropdown(false); navigate('/appeals'); }}>
                        Moderation Appeals
                      </div>
                    </>
                  )}
                  <div style={styles.dropdownDivider} />
                  <div style={{ ...styles.dropdownItem, color: '#d32f2f' }} onClick={handleLogout}>
                    Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <ProfileCompletionModal
          isOpen={showProfileCompletion}
          defaultName={auth.currentUser?.displayName || ''}
          onClose={() => setShowProfileCompletion(false)}
        />
      </nav>
      <div style={{ height: 64 }} />
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  nav: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: 64,
    backgroundColor: 'var(--color-surface)',
    borderBottom: '1px solid var(--color-border)',
    zIndex: 1000,
    boxShadow: 'var(--shadow-sm)'
  },
  navContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%',
    width: '100%',
    maxWidth: 1440,
    margin: '0 auto',
    padding: '0 var(--spacing-base)'
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    width: 280
  },
  centerSection: {
    flex: 1,
    maxWidth: 600,
    display: 'flex',
    justifyContent: 'center'
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-md)',
    width: 360,
    justifyContent: 'flex-end'
  },
  logo: {
    fontSize: 24,
    fontWeight: 700,
    color: 'var(--color-primary-700)',
    textDecoration: 'none'
  },
  createButton: {
    padding: '8px 16px',
    backgroundColor: 'var(--color-primary-500)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  iconButton: {
    width: 44,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'background-color var(--transition-fast)'
  },
  profileAvatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    objectFit: 'cover' as const,
    cursor: 'pointer',
    border: '2px solid var(--color-border)'
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 8,
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    minWidth: 220,
    padding: '8px 0',
    maxHeight: 'calc(100dvh - 80px)',
    overflowY: 'auto',
    zIndex: 2000,
    border: '1px solid var(--color-border)'
  },
  dropdownItem: {
    padding: '10px 16px',
    fontSize: 14,
    color: 'var(--color-text-primary)',
    cursor: 'pointer'
  },
  dropdownDivider: {
    height: 1,
    backgroundColor: 'var(--color-border)',
    margin: '8px 0'
  },
  dropdownIcon: {
    width: 18,
    height: 18,
    color: '#0B6B6F',
    marginRight: 8
  },
  menuDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 8,
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-xl)',
    padding: 'var(--spacing-md)',
    maxHeight: 'calc(100dvh - 80px)',
    overflowY: 'auto',
    zIndex: 2000,
    border: '1px solid var(--color-border)',
    display: 'grid',
    gap: 'var(--spacing-sm)'
  },
  menuTile: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px 10px',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    background: 'var(--color-surface-alt, #f9fafb)',
    border: '1px solid var(--color-border)'
  },
  menuTileIcon: {
    width: 22,
    height: 22,
    color: 'var(--color-primary-600)',
    marginBottom: 6
  },
  menuTileLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--color-text-primary)',
    letterSpacing: 0.3,
    textAlign: 'center' as const
  },
  themeToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    borderRadius: 999,
    border: '1px solid var(--color-border)',
    background: 'var(--color-primary-50)',
    cursor: 'pointer'
  },
  unreadBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    minWidth: 18,
    height: 18,
    padding: '0 5px',
    background: 'var(--color-danger-500, #d32f2f)',
    color: '#fff',
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 0 0 2px var(--color-surface)'
  },
  mobileOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(7, 43, 44, 0.6)',
    backdropFilter: 'blur(8px)',
    zIndex: 2100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    padding: '12px 12px calc(12px + env(safe-area-inset-bottom))'
  },
  mobileSheet: {
    width: '100%',
    maxWidth: 520,
    background: 'var(--color-surface)',
    borderRadius: '24px 24px 12px 12px',
    boxShadow: 'var(--shadow-2xl)',
    maxHeight: 'calc(100dvh - 24px - env(safe-area-inset-bottom))',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column' as const
  },
  mobileSheetHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid var(--color-border)'
  },
  mobileSheetTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: 'var(--color-text-primary)'
  },
  mobileCloseButton: {
    width: 38,
    height: 38,
    borderRadius: 999,
    border: '1px solid var(--color-border)',
    background: 'transparent',
    cursor: 'pointer'
  },
  mobileSheetBody: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '16px 20px 20px 20px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 16
  },
  mobileSection: {
    padding: '12px 20px'
  },
  mobileSectionHeader: {
    marginBottom: 8
  },
  mobileSectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: 'uppercase' as const,
    color: 'var(--color-text-secondary)'
  },
  mobileSectionSubtitle: {
    fontSize: 13,
    color: 'var(--color-text-tertiary, #6b7280)'
  },
  mobileSectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 10
  },
  mobileTile: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    border: '1px solid var(--color-border)',
    borderRadius: 16,
    padding: '12px 12px',
    cursor: 'pointer'
  },
  mobileTileIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: 'var(--color-primary-50)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  mobileTileIcon: {
    width: 20,
    height: 20,
    color: 'var(--color-primary-600)'
  },
  mobileTileText: {
    flex: 1
  },
  mobileTileLabel: {
    fontSize: 14,
    fontWeight: 600
  },
  mobileTileDescription: {
    fontSize: 12,
    color: 'var(--color-text-secondary)'
  },
  mobileTileChevron: {
    width: 18,
    height: 18,
    color: 'var(--color-border)'
  },
  supportLinks: {
    borderTop: '1px solid var(--color-border)',
    padding: '12px 20px 20px 20px'
  },
  supportLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 0',
    cursor: 'pointer'
  },
  supportLinkIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 12,
    background: 'var(--color-surface-alt, #f3f4f6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  supportLinkIcon: {
    width: 18,
    height: 18,
    color: 'var(--color-primary-600)'
  },
  supportLinkText: {
    flex: 1
  },
  supportLinkLabel: {
    fontSize: 14,
    fontWeight: 600
  },
  supportLinkSubtitle: {
    fontSize: 12,
    color: 'var(--color-text-secondary)'
  }
};

export default TopNavigation;
